import { onBeforeUnmount, onMounted, type ShallowRef, shallowRef } from 'vue';
import { currentLocale } from '@/app/i18n';
import { router } from '@/app/router';
import { session } from '@/services/Session';

// A final failure screen (payment or print) ends the session by itself after
// this time without a touch.
export const FINAL_SCREEN_AUTO_END_MS = 30_000;

interface UseSessionEndScreenOptions {
  autoEndAfterMs: number;
  screenName: string;
  outcome?: 'completed' | 'print_failed';
}

interface UseSessionEndScreenResult {
  endSessionAndReturn: () => Promise<void>;
  isNavigatingAway: ShallowRef<boolean>;
}

export function useSessionEndScreen({
  autoEndAfterMs,
  screenName,
  outcome = 'completed',
}: UseSessionEndScreenOptions): UseSessionEndScreenResult {
  const isNavigatingAway = shallowRef(false);

  let autoEndTimeout: ReturnType<typeof setTimeout> | null = null;
  let isEndingSession = false;
  let isUnmounted = false;

  function clearAutoEndTimeout(): void {
    if (autoEndTimeout === null) {
      return;
    }
    clearTimeout(autoEndTimeout);
    autoEndTimeout = null;
  }

  async function endSessionAndReturn(): Promise<void> {
    if (isEndingSession || isUnmounted) {
      return;
    }

    isEndingSession = true;
    isNavigatingAway.value = true;
    clearAutoEndTimeout();

    try {
      await session.end(outcome);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      console.warn(`[${screenName}] session.end() failed:`, message);
    }

    if (!isUnmounted) {
      await router.redirectToStep('', {
        omitQuery: true,
        localeCode: currentLocale.value?.code,
      });
    }
  }

  onMounted(() => {
    autoEndTimeout = setTimeout(() => {
      void endSessionAndReturn();
    }, autoEndAfterMs);
  });

  onBeforeUnmount(() => {
    isUnmounted = true;
    clearAutoEndTimeout();
  });

  return {
    endSessionAndReturn,
    isNavigatingAway,
  };
}
